import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  ExternalLink,
  CreditCard,
  ArrowRight,
  AlertCircle,
  Loader2,
  RefreshCw,
  DollarSign,
  Save,
  X
} from "lucide-react";
import { StripeProvider, useStripe, getStripePromise } from "../../../contexts/StripeContext";
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import { api } from "../../../utils/api";

const PaymentSettingsContent = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // ACH payment settings
  const [achEnabled, setAchEnabled] = useState(false);
  const [achPreference, setAchPreference] = useState('both'); // 'both', 'ach_only', 'card_only'
  const [achMinimumAmount, setAchMinimumAmount] = useState(0);
  
  // Event selection
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [savedStatus, setSavedStatus] = useState('');
  
  // Disconnect confirmation
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  
  const { 
    accountStatus, 
    loading, 
    error, 
    connectAccount, 
    disconnectAccount, 
    createAccountLink,
    refreshAccountStatus,
    updatePaymentSettings,
    connectFlowInProgress
  } = useStripe();
  
  const user = useAuth();

  // Check if account is connected - only show as connected if it's active and verified
  const isConnected = accountStatus && 
                     accountStatus.accountId && 
                     accountStatus.status === 'active' && 
                     !connectFlowInProgress;
  
  // Check if account is in pending state
  const isPending = accountStatus && 
                    accountStatus.accountId && 
                    (accountStatus.status === 'pending' || connectFlowInProgress);

  // Handle direct connect
  const handleConnect = async () => {
    try {
      // Connect with user's email (required parameter)
      const userEmail = user?.email || '';
      await connectAccount(userEmail, ''); // Pass email and empty string for business name
    } catch (err) {
      console.error("Error connecting account:", err);
    }
  };

  // Show disconnect confirmation dialog
  const handleShowDisconnectConfirm = () => {
    setShowDisconnectConfirm(true);
  };

  // Cancel disconnect
  const handleCancelDisconnect = () => {
    setShowDisconnectConfirm(false);
  };

  // Confirm and proceed with disconnect
  const handleConfirmDisconnect = async () => {
    try {
      await disconnectAccount();
      setShowDisconnectConfirm(false);
    } catch (err) {
      console.error("Error disconnecting account:", err);
      setShowDisconnectConfirm(false);
    }
  };

  // Handle refresh onboarding
  const handleRefreshOnboarding = async () => {
    try {
      await createAccountLink();
      // The redirect will happen in the createAccountLink function
    } catch (err) {
      console.error("Error creating account link:", err);
    }
  };

  // Fetch event list
  useEffect(() => {
    const fetchEvents = async () => {
      if (!user || !user.isAuthenticated) return;
      
      try {
        setLoadingEvents(true);
        const response = await fetch('/api/events', {
          headers: {
            'Authorization': `Bearer ${user.token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch events');
        }
        
        const data = await response.json();
        if (data.events && Array.isArray(data.events)) {
          setEvents(data.events);
        }
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    
    fetchEvents();
  }, [user]);

  // Handle event selection change
  const handleEventChange = async (eventId) => {
    setSelectedEventId(eventId);
    
    if (!eventId) {
      // If no event is selected, show the global Stripe account settings
      if (accountStatus && accountStatus.paymentSettings) {
        const settings = accountStatus.paymentSettings;
        if (settings.achEnabled !== undefined) setAchEnabled(settings.achEnabled);
        if (settings.achPreference) setAchPreference(settings.achPreference);
        if (settings.achMinimumAmount !== undefined) setAchMinimumAmount(settings.achMinimumAmount);
      }
      return;
    }
    
    // Load the selected event's payment settings
    try {
      const selectedEvent = events.find(evt => evt.eventId === eventId);
      if (selectedEvent && selectedEvent.paymentSettings) {
        setAchEnabled(selectedEvent.paymentSettings.achEnabled || false);
        setAchPreference(selectedEvent.paymentSettings.achPreference || 'card_only');
        setAchMinimumAmount(selectedEvent.paymentSettings.achMinimumAmount || 0);
      } else {
        // Reset to defaults if no settings found
        setAchEnabled(false);
        setAchPreference('card_only');
        setAchMinimumAmount(0);
      }
    } catch (err) {
      console.error('Error loading event payment settings:', err);
    }
  };

  // Handle saving ACH settings
  const handleSaveAchSettings = async () => {
    setSavedStatus('');
    try {
      if (!user || !user.isAuthenticated) {
        throw new Error('You must be logged in to update payment settings');
      }
      
      const settings = {
        achEnabled,
        achPreference,
        achMinimumAmount: parseInt(achMinimumAmount, 10) || 0
      };
      
      // If an event is selected, save to that event
      if (selectedEventId) {
        const { updateEventPaymentSettings } = await import('../../../utils/eventService');
        await updateEventPaymentSettings(selectedEventId, settings);
        setSavedStatus('success');
      } else {
        // Otherwise save to the global Stripe account settings
        await updatePaymentSettings(settings);
        setSavedStatus('success');
      }
      
      // Show success message
      setTimeout(() => {
        setSavedStatus('');
      }, 3000);
    } catch (err) {
      console.error('Error updating ACH settings:', err);
      setSavedStatus('error');
      setTimeout(() => {
        setSavedStatus('');
      }, 3000);
    }
  };

  // Load existing ACH settings when account status loads
  useEffect(() => {
    if (accountStatus && accountStatus.paymentSettings) {
      const settings = accountStatus.paymentSettings;
      if (settings.achEnabled !== undefined) setAchEnabled(settings.achEnabled);
      if (settings.achPreference) setAchPreference(settings.achPreference);
      if (settings.achMinimumAmount !== undefined) setAchMinimumAmount(settings.achMinimumAmount);
    }
  }, [accountStatus]);

  // Refresh account status when component mounts
  useEffect(() => {
    if (user && user.isAuthenticated) {
      refreshAccountStatus();
    }
  }, [user]);

  // The Stripe logo URL - full wordmark logo
  const stripeLogo = "https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg";

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Disconnect Confirmation Dialog */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Disconnect Stripe Account?</h3>
              <button 
                onClick={handleCancelDisconnect}
                className="text-base-content/70 hover:text-base-content"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="mb-6 text-base-content/80">
              Are you sure you want to disconnect your Stripe account? This will prevent processing payments until you reconnect.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDisconnect}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDisconnect}
                className="btn btn-error"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rounded-box p-6 bg-base-100 border border-base-300">
        <div className="flex flex-col lg:flex-row justify-between gap-6">
          <div>
            <h1 className="text-4xl font-bold text-base-content">
              Payment Settings
            </h1>
            <p className="text-base-content/60 mt-2">
              Connect your Stripe account to process payments and receive payouts
            </p>
          </div>
          <div className="flex items-center">
            <button
              onClick={refreshAccountStatus}
              className="btn btn-primary"
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              )}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="rounded-box p-6 bg-base-100 border border-base-300 flex justify-center items-center py-8">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <span className="ml-2 text-base-content/70">
            Loading payment information...
          </span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="rounded-box p-6 bg-base-100 border border-base-300">
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>Error: {error}</span>
            </div>
            <button 
              onClick={refreshAccountStatus}
              className="mt-2 text-sm underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Connect Flow In Progress State */}
      {connectFlowInProgress && !loading && !error && (
        <div className="rounded-box p-6 bg-base-100 border border-base-300">
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 text-yellow-700 dark:text-yellow-300">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>Stripe account setup in progress. If you closed the Stripe window, please click below to continue.</span>
            </div>
            <button 
              onClick={handleRefreshOnboarding}
              className="mt-2 btn btn-warning btn-sm"
            >
              Continue Setup
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!loading && !error && (
        <div className="space-y-8">
          {/* Stripe Connect Section */}
          <div className="rounded-box p-6 bg-base-100 border border-base-300">
            {!isConnected && !isPending ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-24 h-16 flex items-center justify-center">
                    <img 
                      src={stripeLogo} 
                      alt="Stripe" 
                      className="h-8 object-contain"
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-medium text-base-content">Stripe</h2>
                    <p className="text-sm text-base-content/70">Connect your Stripe account to process payments</p>
                  </div>
                </div>
                <button
                  onClick={handleConnect}
                  className="btn btn-primary"
                  disabled={connectFlowInProgress}
                >
                  Connect
                </button>
              </div>
            ) : isPending ? (
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 flex items-center justify-center">
                      <img 
                        src={stripeLogo} 
                        alt="Stripe" 
                        className="h-8 object-contain"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-medium text-base-content">Stripe</h2>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-warning/20 text-warning">
                          PENDING
                        </span>
                      </div>
                      <p className="text-sm text-base-content/70">
                        Your Stripe account is partially connected. Please complete the setup process.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleRefreshOnboarding}
                    className="btn btn-warning"
                  >
                    Complete Setup
                  </button>
                </div>

                <div className="border-t border-base-300 mt-6 pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-base-content/70">Account</p>
                      <p className="text-base font-medium">{accountStatus?.accountId || "Pending"}</p>
                    </div>
                    <div className="px-3 py-1 rounded-full text-xs font-medium bg-warning/20 text-warning">
                      Requires Attention
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-16 flex items-center justify-center">
                      <img 
                        src={stripeLogo} 
                        alt="Stripe" 
                        className="h-8 object-contain"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-medium text-base-content">Stripe</h2>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-success/20 text-success">
                          CONNECTED
                        </span>
                      </div>
                      <p className="text-sm text-base-content/70">
                        Visit <a href="https://stripe.com/pricing" target="_blank" rel="noopener noreferrer" className="text-primary">Stripe</a> for more information.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleShowDisconnectConfirm}
                    className="btn btn-ghost text-base-content/70 hover:text-error"
                  >
                    Disconnect
                  </button>
                </div>

                <div className="border-t border-base-300 mt-6 pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-base-content/70">Account</p>
                      <p className="text-base font-medium">{accountStatus.accountId}</p>
                    </div>
                    {(accountStatus.requirements && 
                     accountStatus.requirements.currentlyDue && 
                     accountStatus.requirements.eventuallyDue && 
                     accountStatus.requirements.pastDue && 
                     (accountStatus.requirements.currentlyDue.length > 0 || 
                      accountStatus.requirements.eventuallyDue.length > 0 || 
                      accountStatus.requirements.pastDue.length > 0)) && (
                      <div className="px-3 py-1 rounded-full text-xs font-medium bg-warning/20 text-warning">
                        Requires Attention
                      </div>
                    )}
                    {(!accountStatus.requirements || 
                      (accountStatus.requirements.currentlyDue && 
                       accountStatus.requirements.currentlyDue.length === 0 && 
                       accountStatus.requirements.eventuallyDue && 
                       accountStatus.requirements.eventuallyDue.length === 0 && 
                       accountStatus.requirements.pastDue && 
                       accountStatus.requirements.pastDue.length === 0)) && 
                       accountStatus.status === 'active' && (
                      <div className="px-3 py-1 rounded-full text-xs font-medium bg-success/20 text-success">
                        Ready
                      </div>
                    )}
                  </div>
                </div>

                {/* Requirements if needed */}
                {accountStatus.requirements && 
                 accountStatus.requirements.currentlyDue && 
                 accountStatus.requirements.eventuallyDue && 
                 accountStatus.requirements.pastDue && 
                 (accountStatus.requirements.currentlyDue.length > 0 || 
                  accountStatus.requirements.eventuallyDue.length > 0 || 
                  accountStatus.requirements.pastDue.length > 0) && (
                  <div className="mt-4 p-4 border rounded-lg bg-warning/10 border-warning/20">
                    <h4 className="font-medium mb-2 text-warning">
                      Account Requirements
                    </h4>
                    <ul className="list-disc pl-5 text-sm space-y-1 text-base-content">
                      {accountStatus.requirements.currentlyDue && accountStatus.requirements.currentlyDue.map((item, i) => (
                        <li key={`current-${i}`}>{item} (currently due)</li>
                      ))}
                      {accountStatus.requirements.eventuallyDue && accountStatus.requirements.eventuallyDue.map((item, i) => (
                        <li key={`eventual-${i}`}>{item} (eventually due)</li>
                      ))}
                      {accountStatus.requirements.pastDue && accountStatus.requirements.pastDue.map((item, i) => (
                        <li key={`past-${i}`}>{item} (past due)</li>
                      ))}
                    </ul>
                    <button
                      onClick={handleRefreshOnboarding}
                      className="mt-3 btn btn-warning btn-sm"
                    >
                      Complete Requirements
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ACH Payment Settings - Only show if fully connected */}
          {isConnected && (
            <div className="rounded-box p-6 bg-base-100 border border-base-300">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-medium text-base-content">
                    ACH Direct Debit Settings
                  </h2>
                  <p className="text-sm text-base-content/70">
                    Configure ACH bank transfers to reduce payment processing fees
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-success/10">
                  <DollarSign className="w-6 h-6 text-success" />
                </div>
              </div>

              {/* Event Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 text-base-content">
                  Settings Scope
                </label>
                <select
                  value={selectedEventId}
                  onChange={(e) => handleEventChange(e.target.value)}
                  className="select select-bordered w-full"
                >
                  <option value="">Global Account Settings (applies to all events)</option>
                  {events.map(event => (
                    <option key={event.eventId} value={event.eventId}>
                      {event.city} - {new Date(event.startDate).toLocaleDateString()}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-base-content/70">
                  Select "Global Account Settings" to apply to all events, or choose a specific event to override settings
                </p>
              </div>

              <div className="space-y-5">
                {/* Enable ACH Payments */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-base-content">
                      ACH Direct Debit
                    </h3>
                    <p className="text-sm text-base-content/70">
                      Allow customers to pay directly from their bank account
                    </p>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={achEnabled}
                      onChange={(e) => setAchEnabled(e.target.checked)}
                    />
                  </label>
                </div>

                {/* ACH Settings (visible when enabled) */}
                {achEnabled && (
                  <div className="rounded-lg p-4 bg-base-200">
                    {/* Payment Method Preference */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2 text-base-content">
                        Payment Method Preference
                      </label>
                      <select
                        value={achPreference}
                        onChange={(e) => setAchPreference(e.target.value)}
                        className="select select-bordered w-full"
                      >
                        <option value="both">Show both ACH and Card options</option>
                        <option value="ach_only">ACH only (no credit card option)</option>
                        <option value="card_only">Card only (default)</option>
                        <option value="ach_preferred">Show both but recommend ACH</option>
                        <option value="threshold">Show ACH only for amounts above threshold</option>
                      </select>
                    </div>

                    {/* Minimum Amount Threshold (visible for threshold option) */}
                    {achPreference === 'threshold' && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2 text-base-content">
                          Minimum Amount for ACH ($)
                        </label>
                        <input
                          type="number"
                          value={achMinimumAmount}
                          onChange={(e) => setAchMinimumAmount(e.target.value)}
                          min="0"
                          step="1"
                          className="input input-bordered w-full"
                        />
                        <p className="mt-1 text-xs text-base-content/70">
                          Only show ACH as an option when the amount exceeds this threshold
                        </p>
                      </div>
                    )}

                    {/* Fee Comparison */}
                    <div className="p-3 rounded-md bg-info/10 border border-info/20">
                      <h4 className="text-sm font-medium mb-2 text-info">
                        Fee Comparison
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs font-medium text-base-content">Credit Card</p>
                          <p className="text-sm text-base-content/70">2.9% + $0.30</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-base-content">ACH Direct Debit</p>
                          <p className="text-sm text-base-content/70">0.8% (capped at $5)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Save Button and Status */}
                <div className="flex justify-end items-center mt-4">
                  {savedStatus === 'success' && (
                    <span className="mr-3 text-sm text-success">
                      Settings saved successfully!
                    </span>
                  )}
                  {savedStatus === 'error' && (
                    <span className="mr-3 text-sm text-error">
                      Error saving settings. Please try again.
                    </span>
                  )}
                  <button
                    onClick={handleSaveAchSettings}
                    className="btn btn-primary"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {selectedEventId ? 'Save Event Settings' : 'Save Global Settings'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PaymentSettings = () => {
  return (
    <StripeProvider>
      <Elements stripe={getStripePromise()}>
        <PaymentSettingsContent />
      </Elements>
    </StripeProvider>
  );
};

export default PaymentSettings;